import { LitElement, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { Api } from "../api";
import { KeywordList } from "../model/keyword_list";
import { KeywordListRepo } from "../repo/keyword_list_repo";
import { addCircleFillIcon, searchIcon, settingsIcon, deleteIcon, mailIcon, eyeOpenIcon, eyeClosedIcon } from "../utils/icons.js";
import { Store } from "../utils/store.js";
import { maskText } from "../utils/utils.js";
import { MAX_LISTS_PER_USER } from "../utils/limits.js";

@customElement("dashboard-drawer-component")
export class DashboardDrawerComponent extends LitElement {
    @property({ type: String }) selectedListId: string | null = null;
    @state() lists: KeywordList[] = [];
    @state() private blurEnabled = false;
    private repo = new KeywordListRepo();

    createRenderRoot() {
        return this;
    }

    constructor() {
        super();
        this.loadCachedLists();
    }

    async connectedCallback() {
        super.connectedCallback();
        await this.loadLists();

        this.blurEnabled = Store.getSettings().blurSensitiveData ?? false;

        // Listen for blur toggle events
        window.addEventListener("blur-toggle", this.handleBlurToggle);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        window.removeEventListener("blur-toggle", this.handleBlurToggle);
    }

    private handleBlurToggle = (event: Event) => {
        const customEvent = event as CustomEvent;
        this.blurEnabled = customEvent.detail.enabled;
        this.requestUpdate();
    };

    private toggleBlur = () => {
        this.blurEnabled = !this.blurEnabled;
        Store.setSettings({
            ...Store.getSettings(),
            blurSensitiveData: this.blurEnabled,
        });
        // Dispatch a custom event so other components can react
        window.dispatchEvent(new CustomEvent("blur-toggle", { detail: { enabled: this.blurEnabled } }));
        this.requestUpdate();
    };

    isSearchPage() {
        return window.location.pathname.includes("/search");
    }

    isSettingsPage() {
        return window.location.pathname.includes("/settings");
    }

    selectSearch() {
        window.location.href = "/search";
    }

    selectSettings() {
        window.location.href = "/settings";
    }

    selectFeedback() {
        window.open("mailto:denis@andrasec.com?subject=AppShovel Feedback", "_blank");
    }

    loadCachedLists() {
        const cachedLists = this.repo.getCachedKeywordList();
        if (cachedLists instanceof Error) {
            console.error("Error fetching cached lists", cachedLists.message);
        } else {
            this.lists = cachedLists;
        }
    }

    async loadLists() {
        const lists = await this.repo.getAllKeywordLists();
        if (lists instanceof Error) {
            console.error("Error fetching lists", lists.message);
        } else {
            this.lists = lists;
        }
    }

    private get isAtListLimit(): boolean {
        return this.lists.length >= MAX_LISTS_PER_USER;
    }

    async createList() {
        if (this.isAtListLimit) {
            alert(`Maximum of ${MAX_LISTS_PER_USER} lists reached.`);
            return;
        }
        try {
            const list = await Api.listCreate();
            if (list instanceof Error) {
                console.error("Error creating list", list.message);
                return;
            }
            await this.selectList(list.id);
        } catch (error) {
            console.error("Error creating list:", error);
        }
    }

    async deleteList(listId: string) {
        if (confirm("Are you sure you want to delete this list?")) {
            await Api.listDelete(listId);
            await this.loadLists();
            window.location.href = `/search`;
        }
    }

    async selectList(listId: string) {
        window.location.href = `/list/${listId}`;
    }

    render() {
        return html`
            <aside class="bg-base-100 min-h-screen w-90 border-r border-base-200 flex flex-col">
                <div class="navbar p-4 flex justify-between items-center">
                    <div class="flex items-center gap-4">
                        <img src="/logo.svg" alt="AppShovel Logo" class="w-8 h-8" />
                        <a class="text-3xl font-bold" href="/">AppShovel</a>
                    </div>
                    <button
                        class="btn btn-square btn-ghost"
                        @click=${(e: Event) => {
                            e.stopPropagation();
                            this.toggleBlur();
                        }}
                        aria-label="${this.blurEnabled ? "Disable blur mode" : "Enable blur mode"}"
                    >
                        <div class="w-6 h-6">${this.blurEnabled ? eyeClosedIcon : eyeOpenIcon}</div>
                    </button>
                </div>

                <hr class="solid border-t-1 border-base-200 mx-4" />

                <ul class="menu menu-vertical w-full space-y-2 p-4 flex-grow">
                    <li>
                        <div
                            class="flex flex-row items-center ${this.isSearchPage() ? "menu-active" : ""} space-x-2"
                            @click=${() => this.selectSearch()}
                        >
                            <div class="w-6 h-6 text-primary">${searchIcon}</div>
                            <a class="text-lg">Keyword Search</a>
                        </div>
                    </li>
                    <li>
                        <div
                            class="flex flex-row items-center ${this.isSettingsPage() ? "menu-active" : ""} space-x-2"
                            @click=${() => this.selectSettings()}
                        >
                            <div class="w-6 h-6 text-primary">${settingsIcon}</div>
                            <a class="text-lg">Settings</a>
                        </div>
                    </li>

                    <li class="flex flex-row items-center w-full justify-between">
                        <div class="menu-title">Keyword Lists</div>
                        <button
                            class="btn btn-sm btn-circle btn-ghost text-primary"
                            @click=${this.createList}
                            ?disabled=${this.isAtListLimit}
                            title=${this.isAtListLimit ? `Maximum of ${MAX_LISTS_PER_USER} lists reached` : "Create new list"}
                        >
                            <div class="w-8 h-8 mx-auto my-auto">${addCircleFillIcon}</div>
                        </button>
                    </li>

                    ${this.lists.map(
                        (list) => html`
                            <li>
                                <div
                                    class="flex flex-row items-center justify-between ${this.selectedListId === list.id ? "menu-active" : ""} group"
                                    @click=${() => this.selectList(list.id)}
                                >
                                    <div class="flex flex-col">
                                        <a
                                            class="text-lg ${this.blurEnabled ? "select-none" : ""}"
                                            style="${this.blurEnabled ? "filter: blur(4px);" : ""}"
                                            >${this.blurEnabled ? maskText(list.name) : list.name}</a
                                        >
                                        ${list?.app
                                            ? html`
                                                  <div
                                                      class="text-sm text-primary ${this.blurEnabled ? "select-none" : ""}"
                                                      style="${this.blurEnabled ? "filter: blur(4px);" : ""}"
                                                  >
                                                      ${this.blurEnabled
                                                          ? maskText(list?.app?.localization?.title ?? "")
                                                          : list?.app?.localization?.title ?? ""}
                                                  </div>
                                              `
                                            : html``}
                                    </div>
                                    <button
                                        class="btn btn-sm btn-circle btn-ghost opacity-0 group-hover:opacity-100"
                                        @click=${() => this.deleteList(list.id)}
                                    >
                                        <div class="w-4 h-4">${deleteIcon}</div>
                                    </button>
                                </div>
                            </li>
                        `
                    )}
                </ul>

                <hr class="solid border-t-1 border-base-200 mx-4" />

                <ul class="menu menu-vertical w-full space-y-2 p-4">
                    <li>
                        <div class="flex flex-row items-center space-x-2" @click=${() => this.selectFeedback()}>
                            <div class="w-6 h-6 text-primary">${mailIcon}</div>
                            <a class="text-lg">Feedback</a>
                        </div>
                    </li>
                </ul>
            </aside>
        `;
    }
}
